import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useServices } from '../context/ServiceContext';
import api from '../api/client';
import './ToolsPanel.css';
import { getCompanyProfile } from '../config/company';

const TOOLS = [
  { id:'reports',   label:'Reports',          desc:'Download branded PDF reports' },
  { id:'services',  label:'Service Types',    desc:'Manage your service catalogue' },
  { id:'import',    label:'Import Leads',     desc:'Upload CSV file' },
  { id:'export',    label:'Export Leads',     desc:'Download as CSV' },
  { id:'exportall', label:'Export All Data',  desc:'Full data backup JSON' },
  { id:'meta',      label:'Meta Lead Ads',    desc:'Auto-import Facebook & Instagram leads' },
  { id:'notes',     label:'Quick Notes',      desc:'Scratch pad for ideas' },
  { id:'invoice',   label:'Invoice Generator',desc:'Create & print invoices' },
];

function MetaLeadAds() {
  const [conns, setConns] = useState([]);
  const [pageId, setPageId] = useState('');
  const [token, setToken] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api.meta.status().then(r => setConns(r.connections || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const connect = async () => {
    if (!pageId.trim() || !token.trim()) { setMsg('Enter your Page ID and access token.'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await api.meta.connect({ pageId: pageId.trim(), accessToken: token.trim() });
      setMsg(`Connected "${r.pageName}". New leads will appear in your Leads automatically.`);
      setPageId(''); setToken(''); load();
    } catch (e) { setMsg(e.message || 'Could not connect. Check the Page ID and token.'); }
    setBusy(false);
  };
  const disconnect = async (pid) => {
    if (!window.confirm('Disconnect this page? New leads will stop importing.')) return;
    try { await api.meta.disconnect(pid); load(); } catch (e) { alert(e.message); }
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Meta Lead Ads <span className="tp-badge">{conns.length}</span></div>
      <div className="tp-export-info">Connect your Facebook Page so leads from your Facebook &amp; Instagram lead-form ads land in your Leads automatically — no manual entry.</div>
      {conns.length > 0 && (
        <div style={{ margin: '12px 0' }}>
          {conns.map(c => (
            <div key={c.page_id} className="tp-svc-item">
              <span className="tp-svc-name">{c.page_name || c.page_id} <span style={{ opacity: .5, fontSize: 11 }}>· {c.leads_count} leads imported</span></span>
              <div className="tp-svc-actions"><div className="tp-svc-btn tp-svc-del" onClick={() => disconnect(c.page_id)}>Disconnect</div></div>
            </div>
          ))}
        </div>
      )}
      <div className="tp-inv-grp"><label className="tp-inv-lbl">Facebook Page ID</label><input className="tp-inv-inp" placeholder="e.g. 1029384756" value={pageId} onChange={e => setPageId(e.target.value)} /></div>
      <div className="tp-inv-grp"><label className="tp-inv-lbl">Page access token</label><input className="tp-inv-inp" placeholder="Paste your Page access token" value={token} onChange={e => setToken(e.target.value)} /></div>
      <div className="tp-export-btn" style={{ marginTop: 8, opacity: busy ? .6 : 1 }} onClick={busy ? undefined : connect}>{busy ? 'Connecting…' : 'Connect page'}</div>
      {msg && <div className="tp-status">{msg}</div>}
      <div className="tp-notes-hint" style={{ marginTop: 12, lineHeight: 1.6 }}>Find these in Meta Business Suite → your Page → Settings, where you generate a Page access token with leads access. Need a hand? Contact Ryzenforge support.</div>
    </div>
  );
}

function ServiceTypes() {
  const { services, add, remove, edit } = useServices();
  const [newSvc, setNewSvc] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Service Types <span className="tp-badge">{services.length}</span></div>
      <div className="tp-svc-list">
        {services.map(s=>(
          <div key={s} className="tp-svc-item">
            {editing===s?(
              <input className="tp-svc-inp" value={editVal} autoFocus onChange={e=>setEditVal(e.target.value)}
                onBlur={()=>{edit(s,editVal);setEditing(null);}}
                onKeyDown={e=>{if(e.key==='Enter'){edit(s,editVal);setEditing(null);}if(e.key==='Escape')setEditing(null);}}/>
            ):(
              <span className="tp-svc-name" onClick={()=>{setEditing(s);setEditVal(s);}}>{s}</span>
            )}
            <div className="tp-svc-actions">
              <div className="tp-svc-btn" onClick={()=>{setEditing(s);setEditVal(s);}}>✏</div>
              <div className="tp-svc-btn tp-svc-del" onClick={()=>remove(s)}>✕</div>
            </div>
          </div>
        ))}
      </div>
      <div className="tp-svc-add-row">
        <input className="tp-svc-add-inp" placeholder="Add new service type…" value={newSvc}
          onChange={e=>setNewSvc(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'){add(newSvc);setNewSvc('');}}}/>
        <div className="tp-svc-add-btn" onClick={()=>{add(newSvc);setNewSvc('');}}>+ Add</div>
      </div>
    </div>
  );
}

function ImportLeads() {
  const fileRef = useRef();
  const [status, setStatus] = useState('');
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStatus(`"${file.name}" selected — go to Leads page to import`);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Import Leads</div>
      <div className="tp-upload-zone" onClick={()=>fileRef.current.click()}>
        <div className="tp-upload-label">Click to select CSV file</div>
        <div className="tp-upload-hint">Use the Import button on the Leads page for full import</div>
        <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleFile}/>
      </div>
      {status && <div className="tp-status">{status}</div>}
      <a className="tp-template-link" href="data:text/csv,Full Name,Phone Number,Lead Type,Business Type,Lead Quality,Budget,Status,Follow Up 1,Notes 1" download="leads_template.csv">
        Download CSV template
      </a>
    </div>
  );
}

function ExportLeads() {
  const handleExport = () => {
    api.leads.list()
      .then(leads => {
        const headers = ['Name','Company','Phone','Email','Source','Status','Budget','Service','Location','Assigned','Follow Up','Notes'];
        const rows = leads.map(l=>[l.name,l.company,l.phone,l.email,l.source,l.status,l.value,l.service,l.location,l.assigned_name,l.follow_up_date,l.notes].map(v=>`"${v||''}"`).join(','));
        const csv = [headers.join(','),...rows].join('\n');
        const blob = new Blob([csv],{type:'text/csv'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href=url; a.download='leads_export.csv'; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert('No leads data found'));
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export Leads</div>
      <div className="tp-export-info">Downloads all your leads as a CSV file.</div>
      <div className="tp-export-btn" onClick={handleExport}>Download Leads CSV</div>
    </div>
  );
}

function ExportAll() {
  const handleExportAll = () => {
    fetch(api.backup.exportUrl(), { headers: { Authorization: `Bearer ${api.getToken()}` } })
      .then(res => { if (!res.ok) throw new Error('Export failed'); return res.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ryzenos-backup-' + new Date().toISOString().split('T')[0] + '.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(err => alert('Export failed: ' + err.message));
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export All Data</div>
      <div className="tp-export-info">Full backup including leads, clients, invoices, team, and projects as JSON.</div>
      <div className="tp-export-btn" onClick={handleExportAll}>Download Full Backup</div>
    </div>
  );
}

function QuickNotes() {
  const [notes, setNotes] = useState(()=>localStorage.getItem('ryzenos_notes')||'');
  const save = (v) => { setNotes(v); localStorage.setItem('ryzenos_notes',v); };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Quick Notes</div>
      <textarea className="tp-notes" placeholder="Jot anything down…" value={notes} onChange={e=>save(e.target.value)} rows={10}/>
      <div className="tp-notes-hint">Notes are saved automatically.</div>
    </div>
  );
}

function InvoiceGenerator({ onClose }) {
  const company = getCompanyProfile();
  const today = new Date();
  const dateStr = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${today.getFullYear()}`;

  const [form, setForm] = React.useState({
    invoiceNo: 'INV-' + String(Date.now()).slice(-6),
    date: dateStr,
    selectedClient: '',
    clientName: '', clientCompany: '', clientAddress: '', clientGstin: '', clientPhone: '', clientEmail: '',
    service: '', description: '', amount: '', gst: '18', taxMode: 'cgst_sgst',
  });
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const [clients, setClients] = React.useState([]);
  React.useEffect(() => { api.clients.list().then(setClients).catch(()=>{}); }, []);

  const { services } = useServices();

  const selectClient = (val) => {
    const c = clients.find(cl => String(cl.id) === String(val));
    setForm(p => ({
      ...p,
      selectedClient: val,
      ...(c ? {
        clientName: c.name || '',
        clientCompany: c.company || '',
        clientAddress: c.location || c.address || '',
        clientGstin: c.gstin || '',
        clientPhone: c.phone || '',
        clientEmail: c.email || '',
        service: c.service || p.service,
        amount: c.value ? String(c.value) : p.amount,
      } : {}),
    }));
  };

  const sub = parseFloat(form.amount) || 0;
  const gp  = parseFloat(form.gst) || 0;
  const gstAmt = sub * gp / 100;
  const half = gstAmt / 2;
  const total = sub + gstAmt;
  const fmt = (v) => 'Rs. ' + (Math.round(v*100)/100).toLocaleString('en-IN');

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const cName = company.name || 'Your Company';
    const cAddr = [company.address, [company.city, company.state, company.pincode].filter(Boolean).join(' ')].filter(Boolean).join('<br/>');
    const cMeta = [company.phone, company.email, company.website].filter(Boolean).join(' | ');
    const cGst  = company.gstin ? 'GSTIN: ' + company.gstin : '';
    const bank  = company.bank || {};
    const hasBank = bank.accNumber || bank.accName || bank.bank || bank.ifsc;
    const logoHtml = company.logo
      ? '<img src="' + company.logo + '" class="logo" alt="' + cName + '"/>'
      : '<div class="logo-txt">' + cName + '</div>';

    const clName = form.clientName || 'Client';
    const clBits = [form.clientCompany, form.clientAddress, form.clientGstin ? 'GSTIN: ' + form.clientGstin : '', form.clientPhone, form.clientEmail].filter(Boolean).join('<br/>');
    const svc = form.service || 'Service';
    const dsc = form.description || '';

    let taxRows = '';
    if (gstAmt > 0) {
      if (form.taxMode === 'igst') {
        taxRows = '<div class="tr"><span>IGST (' + gp + '%)</span><span>' + fmt(gstAmt) + '</span></div>';
      } else {
        taxRows =
          '<div class="tr"><span>CGST (' + (gp/2) + '%)</span><span>' + fmt(half) + '</span></div>' +
          '<div class="tr"><span>SGST (' + (gp/2) + '%)</span><span>' + fmt(half) + '</span></div>';
      }
    }

    const bankHtml = hasBank
      ? '<div class="bd"><div class="pb"><div class="pbt">Payment Details</div>' +
        (bank.accName   ? '<div class="pr"><div class="prl">Account name</div><div class="prv">' + bank.accName + '</div></div>' : '') +
        (bank.accNumber ? '<div class="pr"><div class="prl">A/C Number</div><div class="prv">' + bank.accNumber + '</div></div>' : '') +
        (bank.bank      ? '<div class="pr"><div class="prl">Bank</div><div class="prv">' + bank.bank + '</div></div>' : '') +
        (bank.ifsc      ? '<div class="pr"><div class="prl">IFSC</div><div class="prv">' + bank.ifsc + '</div></div>' : '') +
        (bank.branch    ? '<div class="pr"><div class="prl">Branch</div><div class="prv">' + bank.branch + '</div></div>' : '') +
        '</div><div class="tsWrap">' + tableHtml() + '</div></div>'
      : '<div class="ts">' + tableHtml() + '</div>';

    function tableHtml() {
      return '<table><thead><tr><th>Description</th><th>Amount</th></tr></thead>' +
        '<tbody><tr><td><div class="sn">' + svc + '</div>' + (dsc ? '<div class="sd">' + dsc + '</div>' : '') + '</td>' +
        '<td>' + fmt(sub) + '</td></tr></tbody></table>' +
        '<div class="tots"><div class="tr"><span>Sub total</span><span>' + fmt(sub) + '</span></div>' +
        taxRows +
        '<div class="tf"><span class="tfl">Total</span><span class="tfv">Rs ' + (Math.round(total*100)/100).toLocaleString('en-IN') + '/-</span></div></div>';
    }

    win.document.write(
      '<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Invoice ' + form.invoiceNo + '</title>' +
      '<style>*{margin:0;padding:0;box-sizing:border-box}' +
      'body{font-family:Arial,Helvetica,sans-serif;background:#f5f3ef;display:flex;justify-content:center;padding:30px;min-height:100vh}' +
      '.w{background:#fff;border-radius:10px;box-shadow:0 2px 20px rgba(0,0,0,.08);width:760px;overflow:hidden}' +
      '.bar{background:#FF6600;height:8px}' +
      '.hd{display:flex;justify-content:space-between;align-items:center;padding:24px 32px 18px;border-bottom:1px solid #eee}' +
      '.logo{height:34px;max-width:220px;object-fit:contain}.logo-txt{font-size:20px;font-weight:800;color:#111}' +
      '.it{font-size:24px;font-weight:700;color:#111;text-align:right}.it span{color:#FF6600}' +
      '.ino{font-size:11px;color:#999;margin-top:3px;text-align:right}' +
      '.pts{display:grid;grid-template-columns:1fr 1fr;padding:20px 32px;gap:24px;border-bottom:1px solid #eee}' +
      '.pl{font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#bbb;margin-bottom:6px}' +
      '.pn{font-size:13px;font-weight:700;color:#111;margin-bottom:2px}' +
      '.pd{font-size:11.5px;color:#777;line-height:1.7}' +
      '.bd{display:grid;grid-template-columns:210px 1fr;border-bottom:1px solid #eee}' +
      '.pb{padding:18px 16px 18px 32px;border-right:1px solid #eee}' +
      '.pbt{font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#FF6600;margin-bottom:12px}' +
      '.pr{margin-bottom:8px}.prl{font-size:9.5px;color:#bbb}.prv{font-size:12px;font-weight:700;color:#111;margin-top:1px}' +
      '.ts{padding:18px 32px}.tsWrap{padding:18px 32px 18px 16px}' +
      'table{width:100%;border-collapse:collapse}' +
      'th{font-size:9px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#bbb;padding:0 0 8px;text-align:left;border-bottom:1px solid #eee}' +
      'th:last-child,td:last-child{text-align:right}' +
      'td{padding:10px 0;font-size:12.5px;color:#111;border-bottom:.5px solid #f5f5f5;vertical-align:top}' +
      '.sn{font-weight:600}.sd{font-size:10.5px;color:#aaa;margin-top:2px}' +
      '.tots{margin-top:12px}' +
      '.tr{display:flex;justify-content:space-between;padding:4px 0;font-size:12px;color:#888;border-bottom:.5px solid #f5f5f5}' +
      '.tf{display:flex;justify-content:space-between;align-items:center;padding:10px 0 0}' +
      '.tfl{font-size:13px;font-weight:600;color:#111}' +
      '.tfv{background:#FF6600;border-radius:6px;color:#fff;font-size:13px;font-weight:700;padding:7px 16px}' +
      '.note{padding:12px 32px;font-size:11px;font-style:italic;color:#aaa;border-top:1px solid #eee}' +
      '.ft{padding:14px 32px;font-size:10.5px;color:#aaa;text-align:center;border-top:1px solid #eee}' +
      '@media print{body{background:#fff;padding:0}.w{box-shadow:none;border-radius:0;width:100%}}' +
      '</style></head><body>' +
      '<div class="w"><div class="bar"></div>' +
      '<div class="hd">' + logoHtml +
      '<div><div class="it">Invoice</div><div class="ino">No. ' + form.invoiceNo + '&nbsp;|&nbsp;Date ' + form.date + '</div></div></div>' +
      '<div class="pts">' +
      '<div><div class="pl">From</div><div class="pn">' + cName + '</div>' +
      '<div class="pd">' + (cAddr ? cAddr + '<br/>' : '') + (cMeta ? cMeta + '<br/>' : '') + cGst + '</div></div>' +
      '<div style="text-align:right"><div class="pl">Bill To</div><div class="pn">' + clName + '</div>' +
      '<div class="pd">' + clBits + '</div></div></div>' +
      bankHtml +
      '<div class="note">Thank you for your business. Please make payment as per the details above.</div>' +
      '<div class="ft">' + cName + (cMeta ? '&nbsp;|&nbsp;' + cMeta : '') + '</div>' +
      '</div></body></html>'
    );
    win.document.close();
    setTimeout(function(){ win.print(); }, 400);
  };

  return (
    <div className="tp-section">
      <div className="tp-sec-title">Invoice Generator</div>
      <div className="tp-inv-two">
        <div className="tp-inv-form">
          <div className="tp-inv-row">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Invoice Number</label><input className="tp-inv-inp" value={form.invoiceNo} onChange={e=>setF('invoiceNo',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Date</label><input className="tp-inv-inp" value={form.date} onChange={e=>setF('date',e.target.value)}/></div>
          </div>

          <div className="tp-inv-sec">Bill To</div>
          {clients.length>0 && (
            <div className="tp-inv-grp">
              <label className="tp-inv-lbl">Select from CRM</label>
              <select className="tp-inv-inp" value={form.selectedClient||''} onChange={e=>selectClient(e.target.value)}>
                <option value="">-- Select client or type manually --</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}{c.company?' — '+c.company:''}</option>)}
              </select>
            </div>
          )}
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Client Name</label><input className="tp-inv-inp" placeholder="e.g. John Doe" value={form.clientName} onChange={e=>setF('clientName',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Company Name</label><input className="tp-inv-inp" placeholder="Client company" value={form.clientCompany} onChange={e=>setF('clientCompany',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Address (optional)</label><input className="tp-inv-inp" placeholder="Street, City, State" value={form.clientAddress} onChange={e=>setF('clientAddress',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Client GSTIN (optional)</label><input className="tp-inv-inp" placeholder="Client GSTIN" value={form.clientGstin} onChange={e=>setF('clientGstin',e.target.value)}/></div>

          <div className="tp-inv-sec">Service</div>
          <div className="tp-inv-grp">
            <label className="tp-inv-lbl">Service Title</label>
            {services.length>0 ? (
              <select className="tp-inv-inp" value={services.includes(form.service)?form.service:''} onChange={e=>setF('service',e.target.value)}>
                <option value="">-- Select or type below --</option>
                {services.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            ) : null}
            <input className="tp-inv-inp" style={services.length>0?{marginTop:6}:{}} placeholder="e.g. Web Development, CRM Setup..." value={form.service} onChange={e=>setF('service',e.target.value)}/>
          </div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Description</label><input className="tp-inv-inp" placeholder="Brief description of the work done" value={form.description} onChange={e=>setF('description',e.target.value)}/></div>

          <div className="tp-inv-row">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Amount (INR)</label><input className="tp-inv-inp" type="number" placeholder="e.g. 50000" value={form.amount} onChange={e=>setF('amount',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">GST %</label><input className="tp-inv-inp" type="number" placeholder="18" value={form.gst} onChange={e=>setF('gst',e.target.value)}/></div>
          </div>
          <div className="tp-inv-grp">
            <label className="tp-inv-lbl">Tax type</label>
            <select className="tp-inv-inp" value={form.taxMode} onChange={e=>setF('taxMode',e.target.value)}>
              <option value="cgst_sgst">CGST + SGST (within state)</option>
              <option value="igst">IGST (other state)</option>
            </select>
          </div>

          {gstAmt>0 && (
            <div className="tp-inv-tax-breakdown">
              {form.taxMode==='igst'
                ? <div className="tp-inv-tax-line"><span>IGST ({gp}%)</span><span>{fmt(gstAmt)}</span></div>
                : <>
                    <div className="tp-inv-tax-line"><span>CGST ({gp/2}%)</span><span>{fmt(half)}</span></div>
                    <div className="tp-inv-tax-line"><span>SGST ({gp/2}%)</span><span>{fmt(half)}</span></div>
                  </>}
            </div>
          )}

          <div className="tp-inv-total-row">
            <span>Total</span><span className="tp-inv-total">Rs. {(Math.round(total*100)/100).toLocaleString('en-IN')}</span>
          </div>
          {!company.name && <div className="tp-inv-warn">Add your company details in Settings → Company so they appear on the invoice.</div>}
          <div className="tp-export-btn" style={{marginTop:8}} onClick={handlePrint}>Save as PDF</div>
        </div>
      </div>
    </div>
  );
}

function Reports() {
  const iso = (d) => d.toISOString().slice(0,10);
  const todayStr = iso(new Date());
  const [type, setType] = useState('project');
  const [preset, setPreset] = useState('today');
  const [from, setFrom] = useState(todayStr);
  const [to, setTo] = useState(todayStr);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [business, setBusiness] = useState('all');
  const [bizList, setBizList] = useState([]);
  useEffect(() => { api.settings.get().then(d => setBizList(Array.isArray(d.businesses) ? d.businesses : [])).catch(() => {}); }, []);

  const applyPreset = (p) => {
    setPreset(p);
    const now = new Date();
    if (p === 'today') { setFrom(todayStr); setTo(todayStr); }
    else if (p === 'week') { const s = new Date(); s.setDate(s.getDate()-6); setFrom(iso(s)); setTo(todayStr); }
    else if (p === 'month') { const s = new Date(now.getFullYear(), now.getMonth(), 1); setFrom(iso(s)); setTo(todayStr); }
    // custom: leave the current from/to for the user to edit
  };

  const REPORTS = [
    { id:'project',   label:'Project Report',  desc:'Leads, revenue, attendance, sources, follow-ups, work by employee' },
    { id:'leads',     label:'Leads',           desc:'All leads in the range' },
    { id:'clients',   label:'Clients',         desc:'Your client list' },
    { id:'employees', label:'Employees',       desc:'Your team' },
    { id:'projects',  label:'Projects',        desc:'All projects' },
  ];
  const PRESETS = [ ['today','Today'], ['week','This week'], ['month','This month'], ['custom','Custom'] ];

  const download = async () => {
    setBusy(true); setMsg('');
    try {
      const blob = await api.reports.downloadPdf({ type, from, to, business: business !== 'all' ? business : undefined });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${type}-report-${from}${from!==to?'_to_'+to:''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setMsg('Report downloaded as PDF.');
    } catch (e) { setMsg(e.message || 'Could not generate the report.'); }
    setBusy(false);
  };

  return (
    <div className="tp-section">
      <div className="tp-sec-title">Reports</div>
      <div className="tp-export-info">Download branded PDF reports with your company details, charts and metrics. The Project Report is also emailed to you automatically every day at 5:45 PM.</div>

      <label className="tp-inv-lbl" style={{marginTop:12}}>Report</label>
      <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:4}}>
        {REPORTS.map(r => (
          <button key={r.id} type="button" onClick={()=>setType(r.id)}
            style={{flex:'0 0 auto',padding:'7px 14px',borderRadius:8,cursor:'pointer',lineHeight:1.2,fontFamily:'inherit',
              border:'1px solid '+(type===r.id?'#ff6600':'rgba(128,128,128,.35)'),
              background: type===r.id ? 'rgba(255,102,0,.1)' : 'transparent',
              color: type===r.id ? '#ff6600' : 'inherit', fontWeight:600, fontSize:13}}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="tp-nav-desc" style={{marginBottom:10}}>{REPORTS.find(r=>r.id===type)?.desc}</div>

      <label className="tp-inv-lbl">Period</label>
      <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:8}}>
        {PRESETS.map(([id,label]) => (
          <button key={id} type="button" onClick={()=>applyPreset(id)}
            style={{flex:'0 0 auto',padding:'7px 14px',borderRadius:8,cursor:'pointer',lineHeight:1.2,fontFamily:'inherit',
              border:'1px solid '+(preset===id?'#ff6600':'rgba(128,128,128,.35)'),
              background: preset===id ? 'rgba(255,102,0,.1)' : 'transparent',
              color: preset===id ? '#ff6600' : 'inherit', fontWeight:600, fontSize:13}}>
            {label}
          </button>
        ))}
      </div>
      {preset==='custom' && (
        <div style={{display:'flex',gap:8,marginBottom:8}}>
          <div style={{flex:1}}><label className="tp-inv-lbl">From</label><input className="tp-inv-inp" type="date" value={from} max={to} onChange={e=>setFrom(e.target.value)}/></div>
          <div style={{flex:1}}><label className="tp-inv-lbl">To</label><input className="tp-inv-inp" type="date" value={to} min={from} max={todayStr} onChange={e=>setTo(e.target.value)}/></div>
        </div>
      )}

      {(type==='project' || type==='projects') && bizList.length > 0 && (
        <>
          <label className="tp-inv-lbl">Business</label>
          <select className="tp-inv-inp" value={business} onChange={e=>setBusiness(e.target.value)} style={{marginBottom:8}}>
            <option value="all">All businesses</option>
            {bizList.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </>
      )}

      <div className="tp-export-btn" style={{marginTop:10, opacity: busy ? .6 : 1}} onClick={busy ? undefined : download}>
        {busy ? 'Generating…' : 'Download PDF'}
      </div>
      {msg && <div className="tp-status">{msg}</div>}
    </div>
  );
}

export default function ToolsPanel({ onClose }) {
  const { theme } = useTheme();
  const [active, setActive] = useState('reports');

  const renderContent = () => {
    if (active==='reports')   return <Reports/>;
    if (active==='services')  return <ServiceTypes/>;
    if (active==='import')    return <ImportLeads/>;
    if (active==='export')    return <ExportLeads/>;
    if (active==='exportall') return <ExportAll/>;
    if (active==='meta')      return <MetaLeadAds/>;
    if (active==='notes')     return <QuickNotes/>;
    if (active==='invoice')   return <InvoiceGenerator onClose={onClose}/>;
    return null;
  };

  return (
    <div className="tp-wrap" data-theme={theme}>
      <div className="tp-overlay" onClick={onClose}/>
      <div className="tp-panel" data-theme={theme}>
        <div className="tp-header">
          <span className="tp-title">Tools</span>
          <div className="tp-close" onClick={onClose}>✕</div>
        </div>
        <div className="tp-body">
          <div className="tp-nav">
            {TOOLS.map(t=>(
              <div key={t.id} className={`tp-nav-item${active===t.id?' active':''}`} onClick={()=>setActive(t.id)}>
                <div>
                  <div className="tp-nav-label">{t.label}</div>
                  <div className="tp-nav-desc">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="tp-content">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
